import { type ITenantConfig } from '@/common/types/tenant-config.types';
import { getTenantConfigService } from '@/common/apis/personal-pages/config';
import { API_BASE_URL_EXCEPTIONS } from '@/common/constants/api-base-url-exceptions';
import { isWitroPlatformDomain } from '@/common/constants/white-list-domains.constant';
import { getQueryClient } from './get-query-client.utility';

export interface IConfig extends ITenantConfig {
  domain: string;
  baseUrl: string;
}

interface IDomainConfig {
  getBaseURL: () => Promise<string>;
  getDomain: () => Promise<string>;
  getFullURL: () => Promise<string>;
  isTenant: () => Promise<boolean>;
  getTenantConfig: () => Promise<IConfig>;
}

class DomainConfigManagerClass implements IDomainConfig {
  async getTenantConfig(): Promise<IConfig> {
    const query = getQueryClient();
    const domain = await this.getDomain();
    const baseUrl = await this.getBaseURL();
    // * Get Tenant Config
    const configData = await query.fetchQuery({
      queryKey: ['Tenant-Config', domain],
      queryFn: getTenantConfigService,
    });

    const configs = configData.data;

    const config: IConfig = {
      domain,
      baseUrl,
      ...configs,
    };
    return config;
  }

  async isTenant(): Promise<boolean> {
    try {
      const domain = await this.getDomain();

      return !!domain && !isWitroPlatformDomain(domain);
    } catch {
      return false;
    }
  }

  async getBaseURL(): Promise<string> {
    const domain = await this.getDomain();
    const exception = API_BASE_URL_EXCEPTIONS.find(
      (ex) => ex.domain === domain
    );
    if (exception) {
      return exception.api_url;
    }
    return `https://api.${domain}/api`;
  }

  async getDomain(): Promise<string> {
    let domain = '';
    if (typeof window === 'undefined') {
      const headersModule = await import('next/headers');
      const requestHeaders = await headersModule.headers();
      domain = requestHeaders.get('host') as string;
    } else {
      domain = window.location.host;
    }

    if (!domain) {
      throw new Error('Domain header is missing');
    }

    return domain.trim().replace(/\.$/, '').replace(/^www\./i, '').toLowerCase();
  }

  async getFullURL(): Promise<string> {
    const domain = await this.getDomain();

    // Check if domain is in the dev exceptions list (uses http)
    const isDevelopmentDomain = API_BASE_URL_EXCEPTIONS.some(
      (ex) => ex.domain === domain && ex.domain.includes('localhost')
    );

    const protocol = isDevelopmentDomain ? 'http' : 'https';

    return `${protocol}://${domain}`;
  }
}

export const DomainConfigManager = new DomainConfigManagerClass();
